/**
 * ModeratorPortal — `/moderator` — the community moderation command center
 * for users with `role='moderator'` (admins also welcome).
 *
 * Keeps moderators out of the admin console entirely. The four tiles
 * mirror the four things they actually need to do:
 *
 *   1. Reports queue      — triage flagged posts/comments/DMs
 *   2. User actions       — soft-ban / suspend community members
 *   3. Mod Room           — private staff chat
 *   4. Bug reports (opt.) — see incoming bug reports from members
 *
 * The tiles are just navigational — the underlying pages (AdminReports,
 * UserManagement, ModRoom, AdminBugReports) already implement the
 * moderator-safe behaviors we need. Each of those pages already gates
 * destructive actions to `admin` role only.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ShieldCheck, Flag, UserCog, MessageSquare, Bug, LogOut, ArrowRight, Sunrise } from 'lucide-react';
import MorningBriefModal from '../components/MorningBriefModal';

export default function ModeratorPortal() {
  const { API, user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ reports: 0, bugs: 0, users: 0 });
  const [briefOpen, setBriefOpen] = useState(false);

  // Lightweight counts so the tiles feel alive. Failures are silent —
  // this is a landing page, not a data-critical view.
  useEffect(() => {
    (async () => {
      try {
        const [rep, bugs, users] = await Promise.allSettled([
          axios.get(`${API}/reports?status=open&limit=1`),
          axios.get(`${API}/bug-reports?status=open&limit=1`),
          axios.get(`${API}/admin/users`),
        ]);
        setCounts({
          reports: rep.value?.data?.total ?? rep.value?.data?.reports?.length ?? 0,
          bugs:    bugs.value?.data?.total ?? bugs.value?.data?.bug_reports?.length ?? 0,
          users:   users.value?.data?.users?.length ?? users.value?.data?.length ?? 0,
        });
      } catch {
        /* silent */
      }
    })();
  }, [API]);

  const tiles = [
    {
      key: 'reports',
      title: 'Reports queue',
      desc: 'Triage posts, comments, and DMs flagged by members.',
      icon: Flag,
      to: '/admin/reports',
      badge: counts.reports ? `${counts.reports} open` : null,
      badgeClass: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
      testid: 'mod-tile-reports',
    },
    {
      key: 'users',
      title: 'User actions',
      desc: 'Soft-ban or suspend a community member. Full delete stays with admins.',
      icon: UserCog,
      to: '/admin/users',
      badge: counts.users ? `${counts.users} members` : null,
      badgeClass: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
      testid: 'mod-tile-users',
    },
    {
      key: 'modroom',
      title: 'Mod Room',
      desc: 'Private staff chat — coordinate calls, share screenshots, keep notes.',
      icon: MessageSquare,
      to: '/admin/mod-room',
      badge: null,
      badgeClass: '',
      testid: 'mod-tile-modroom',
    },
    {
      key: 'bugs',
      title: 'Bug reports',
      desc: 'Read incoming bug reports so you can help members while admins fix them.',
      icon: Bug,
      to: '/admin/bug-reports',
      badge: counts.bugs ? `${counts.bugs} open` : null,
      badgeClass: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
      testid: 'mod-tile-bugs',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="moderator-portal">
      <header className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-6 h-6 text-emerald-500" />
            <div>
              <h1 className="text-lg font-bold text-slate-900 dark:text-white">Moderator Portal</h1>
              <p className="text-xs text-slate-500">
                Signed in as {user?.email || 'moderator'} · {user?.role || 'moderator'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
              Community
            </Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="mod-logout">
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Today&apos;s queue</h2>
            <p className="text-sm text-slate-500 mt-1">
              Pick a tile below. Every destructive action is logged — you can review your history from the User actions page.
            </p>
          </div>
          <Button
            onClick={() => setBriefOpen(true)}
            className="gap-2 bg-emerald-600 hover:bg-emerald-700"
            data-testid="mod-open-brief"
          >
            <Sunrise className="w-4 h-4" />
            Read morning brief
          </Button>
        </div>
        <MorningBriefModal open={briefOpen} onOpenChange={setBriefOpen} />
        <div className="grid gap-4 md:grid-cols-2">
          {tiles.map((t) => (
            <Card
              key={t.key}
              className="hover:border-emerald-500 dark:hover:border-emerald-500 transition-colors cursor-pointer"
              onClick={() => navigate(t.to)}
              data-testid={t.testid}
            >
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <t.icon className="w-6 h-6 text-emerald-500" />
                    <CardTitle>{t.title}</CardTitle>
                  </div>
                  {t.badge ? (
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${t.badgeClass}`}>
                      {t.badge}
                    </span>
                  ) : null}
                </div>
                <CardDescription className="pt-1">{t.desc}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  variant="ghost"
                  className="text-emerald-600 dark:text-emerald-400 p-0 h-auto"
                >
                  Open <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
}
